/* eslint-disable max-classes-per-file */
import { norm, divide } from 'mathjs';
import { MaxFlowProblem } from '../typings';

const { floor } = Math;

export function normalize(v: number[]): number[] {
  return divide(v, norm(v, v.length)) as number[];
}

export function augmentCapacityAlongPath(
  problem: MaxFlowProblem,
  path: number[],
  delta: number
): MaxFlowProblem {
  for (let i = 0; i < path.length - 1; i += 1) {
    problem.graph.edges[path[i]][path[i + 1]].residualCapacity -= delta;
    problem.graph.edges[path[i + 1]][path[i]].residualCapacity += delta;
  }
  return problem;
}

export class MinHeap<T> {
  size_ = 0;

  maxSize_: number;

  heap_: { val: T; id: number }[];

  positions_: number[];

  unused_: number[];

  comparator: (a: T, b: T) => number;

  constructor(maxSize: number, comparator?: (a: T, b: T) => number) {
    this.maxSize_ = maxSize;
    this.heap_ = Array.from({ length: maxSize });
    this.positions_ = Array.from({ length: maxSize }, (e, i) => i);
    this.unused_ = Array.from({ length: maxSize }, (e, i) => i);
    if (comparator) this.comparator = comparator;
    else this.comparator = (a: any, b: any) => b - a;
  }

  swap_(idA: number, idB: number) {
    const heapAux = this.heap_[this.positions_[idA]];
    this.heap_[this.positions_[idA]] = this.heap_[this.positions_[idB]];
    this.heap_[this.positions_[idB]] = heapAux;
    const positionsAux = this.positions_[idA];
    this.positions_[idA] = this.positions_[idB];
    this.positions_[idB] = positionsAux;
  }

  size(): number {
    return this.size_;
  }

  maxSize(): number {
    return this.maxSize_;
  }

  insert(item: T): number {
    const id = this.unused_[this.size_];
    this.heap_[this.positions_[id]] = { val: item, id };
    let parentHeapPosition = floor((this.positions_[id] - 1) / 2);
    while (
      parentHeapPosition !== -1 &&
      this.comparator(this.heap_[parentHeapPosition].val, this.heap_[this.positions_[id]].val) < 0
    ) {
      const parentId = this.heap_[parentHeapPosition].id;
      this.swap_(id, parentId);
      parentHeapPosition = floor((this.positions_[id] - 1) / 2);
    }
    this.size_ += 1;
    return id;
  }

  extractMinimum(): T | undefined {
    const { id } = this.heap_[this.size_ - 1];
    this.swap_(this.heap_[0].id, id);
    let childHeapPosition = this.positions_[id] * 2 + 1;
    while (childHeapPosition < this.size_ - 1) {
      if (
        this.comparator(this.heap_[childHeapPosition + 1].val, this.heap_[childHeapPosition].val) >
        0
      ) {
        childHeapPosition += 1;
      }
      if (
        this.comparator(this.heap_[childHeapPosition].val, this.heap_[this.positions_[id]].val) > 0
      ) {
        const childId = this.heap_[childHeapPosition].id;
        this.swap_(id, childId);
        childHeapPosition = this.positions_[childId] * 2 + 1;
      } else {
        break;
      }
    }

    this.unused_[this.size_] = this.heap_[this.size_ - 1].id;
    this.size_ -= 1;
    return this.heap_[this.size_].val;
  }

  decreaseKey(id: number, newVal: T): void {
    this.heap_[this.positions_[id]].val = newVal;
    let parentHeapPosition = floor((this.positions_[id] - 1) / 2);
    while (
      parentHeapPosition !== -1 &&
      this.comparator(this.heap_[parentHeapPosition].val, this.heap_[this.positions_[id]].val) < 0
    ) {
      const parentId = this.heap_[parentHeapPosition].id;
      this.swap_(id, parentId);
      parentHeapPosition = floor((this.positions_[id] - 1) / 2);
    }
  }

  getKey(id: number): T {
    return this.heap_[this.positions_[id]].val;
  }
}

export function ringLIFOSize<T>(q: T[], head: number, tail: number): number {
  return (tail - head + q.length) % q.length;
}

// Returns new tail
export function ringLIFOEnqueue<T>(q: T[], head: number, tail: number, item: T): number {
  if ((tail + 1) % q.length === head) {
    throw Error('Tried to insert into full LIFO queue.');
  }
  q[tail] = item;
  return (tail + 1) % q.length;
}

// Returns [value, new head]
export function ringLIFODequeue<T>(q: T[], head: number, tail: number): [T | null, number] {
  if (head === tail) {
    return [null, head];
  }
  return [q[head], (head + 1) % q.length];
}
